import React from 'react';
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  pointerWithin,
  rectIntersection,
  CollisionDetection,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Box, IconButton, Typography } from '@mui/material';
import { Delete as DeleteIcon, DragIndicator as DragIndicatorIcon } from '@mui/icons-material';

import { SortableItem } from '../DragDrop/SortableItem';
import { QueryExpressionItem } from './QueryExpressionItem';
import { DroppableContainer } from '../DragDrop/DroppableContainer';
import { QueryExpression } from '../../../../types/constants/query-expression';
import { AttributeApiModel } from '../../../../types/models/attribute.model';

const rootContainerId = 'root';

type Props = {
  expressions: QueryExpression[];
  properties: Pick<AttributeApiModel, 'name' | 'type' | 'values'>[];
  onQueryExpressionChange: (key: string, value: string) => void;
  onDeleteSet: (index: string) => void;
  onExpressionsReorder: (newExpressions: QueryExpression[]) => void;
};

export const EditQueryExpressions = ({
  expressions,
  onQueryExpressionChange,
  onDeleteSet,
  onExpressionsReorder,
  properties,
}: Props) => {
  const lastItemId = expressions.length > 0 ? expressions[expressions.length - 1].id : null;
  const [activeId, setActiveId] = React.useState<string | null>(null);

  const findItemById = (id: string | number, items: QueryExpression[] = expressions): QueryExpression | null => {
    const directMatch = items.find((item) => `${item.id}` === `${id}`);
    if (directMatch) {
      return directMatch;
    }

    const nestedResult = items
      .filter((item) => item.expressions && Array.isArray(item.expressions))
      .map((item) => findItemById(id, item.expressions!))
      .find((result) => result !== null);

    return nestedResult ?? null;
  };

  const collisionDetection: CollisionDetection = (args) => {
    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length > 0) {
      return pointerCollisions;
    }
    return rectIntersection(args);
  };

  const findContainer = (
    id: string | number,
    itemsList: QueryExpression[] = expressions,
    parentPath: string = rootContainerId,
  ) => {
    if (itemsList.some((item: QueryExpression) => item.id === `${id}`)) {
      return parentPath;
    }

    const container = itemsList
      .filter((item) => item.expressions && Array.isArray(item.expressions))
      .find((item) => {
        const containerPath = `container-${item.id}`;
        return findContainer(id, item.expressions!, containerPath);
      });

    if (container) {
      const containerPath = `container-${container.id}`;
      return findContainer(id, container.expressions!, containerPath);
    }

    return undefined;
  };

  const findItemInContainer = (
    id: number | string,
    itemsList: QueryExpression[] = expressions,
    path: (number | string)[] = [],
  ): { item: QueryExpression; path: (number | string)[]; container: QueryExpression[] } | null => {
    const itemIndex = itemsList.findIndex((item) => item.id === id);
    if (itemIndex !== -1) {
      return { item: itemsList[itemIndex], path: [...path, itemIndex], container: itemsList };
    }

    const result = itemsList
      .map((item, index) => ({
        item,
        index,
        hasExpressions: item.expressions && Array.isArray(item.expressions),
      }))
      .filter(({ hasExpressions }) => hasExpressions)
      .map(({ item, index }) => findItemInContainer(id, item.expressions!, [...path, index, 'expressions']))
      .find((foundResult) => foundResult !== null);

    return result ?? null;
  };

  const getContainerByPath = (itemsList: QueryExpression[], containerPath: string) => {
    if (containerPath === rootContainerId) {
      return itemsList;
    }

    const containerId = containerPath.replace('container-', '');

    const findContainerRecursive = (list: QueryExpression[]): QueryExpression[] | null => {
      const matchingItem = list.find((item) => item.id === containerId);
      if (matchingItem) {
        return matchingItem.expressions || null;
      }

      const nestedResult = list
        .filter((item) => item.expressions && Array.isArray(item.expressions))
        .map((item) => findContainerRecursive(item.expressions!))
        .find((result) => result !== null);

      return nestedResult ?? null;
    };

    return findContainerRecursive(itemsList);
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(`${event.active.id}`);
  };

  const determineOverContainer = (overId: string | number) => {
    if (overId === rootContainerId) {
      return rootContainerId;
    }
    if (`${overId}`.startsWith('container-')) {
      return `${overId}`;
    }
    return findContainer(`${overId}`);
  };

  const calculateInsertionIndex = (
    over: DragEndEvent['over'],
    destinationContainer: QueryExpression[],
    event: DragEndEvent,
  ) => {
    if (!over) return destinationContainer.length;

    if (!`${over.id}`.startsWith('container-') && over.id !== rootContainerId) {
      const targetIndex = destinationContainer.findIndex((item) => `${item.id}` === `${over.id}`);
      return targetIndex !== -1 ? targetIndex : destinationContainer.length;
    }

    if (over.id === rootContainerId || `${over.id}`.startsWith('container-')) {
      const activatorEvent = event.activatorEvent as MouseEvent | TouchEvent | undefined;
      const pointerY =
        activatorEvent && 'clientY' in activatorEvent ? activatorEvent.clientY + (event.delta?.y || 0) : null;

      if (pointerY !== null && destinationContainer.length > 0) {
        const calculatedIndex = destinationContainer.findIndex((item) => {
          const itemElement = document.querySelector(`[data-id="${item.id}"]`);
          if (itemElement) {
            const rect = itemElement.getBoundingClientRect();
            const itemMiddle = rect.top + rect.height / 2;
            return pointerY < itemMiddle;
          }
          return false;
        });

        return calculatedIndex !== -1 ? calculatedIndex : destinationContainer.length;
      }
    }

    return destinationContainer.length;
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) return;

    const activeContainer = findContainer(`${active.id}`);

    const overContainer = determineOverContainer(over.id);

    if (!activeContainer || !overContainer) return;

    const newExpressions = JSON.parse(JSON.stringify(expressions));

    if (activeContainer === overContainer) {
      const container = getContainerByPath(newExpressions, activeContainer);
      if (!container) return;

      const oldIndex = container.findIndex(({ id }) => `${id}` === `${active.id}`);
      const newIndex = container.findIndex(({ id }) => `${id}` === `${over.id}`);

      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        const [movedItem] = container.splice(oldIndex, 1);
        container.splice(newIndex, 0, movedItem);
        onExpressionsReorder(newExpressions);
      }
      return;
    }

    const source = findItemInContainer(active.id, newExpressions);
    if (!source) return;

    source.container.splice(source.container.indexOf(source.item), 1);

    const destinationContainer = getContainerByPath(newExpressions, overContainer);
    if (!destinationContainer) return;

    const insertIndex = calculateInsertionIndex(over, destinationContainer, event);

    destinationContainer.splice(insertIndex, 0, source.item);

    onExpressionsReorder(newExpressions);
  };

  const renderItemRecursive = (
    item: QueryExpression,
    index: number,
    parentPath: string = '',
    containerExpressions: QueryExpression[] = expressions,
    ancestorIds: string[] = [],
  ) => {
    if (item.expressions && Array.isArray(item.expressions)) {
      const containerId = `container-${item.id}`;
      const isEmpty = item.expressions.length === 0;
      const newAncestorIds = [...ancestorIds, item.id];

      return (
        <SortableItem key={item.id} id={item.id} isDragHandle>
          {({ dragHandleProps }) => (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                padding: 2,
                border: '1px solid #ccc',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton {...dragHandleProps} sx={{ cursor: 'grab', '&:active': { cursor: 'grabbing' } }}>
                  <DragIndicatorIcon />
                </IconButton>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
                <Typography sx={{ fontSize: 24 }}>(</Typography>
              </Box>
              <DroppableContainer id={containerId} isEmpty={isEmpty}>
                <SortableContext
                  items={(item.expressions ?? []).map(({ id }) => id)}
                  strategy={verticalListSortingStrategy}
                >
                  {(item.expressions ?? []).map((nestedItem: QueryExpression, nestedIndex: number) => {
                    const nestedPath = parentPath ? `${parentPath}.${index}.expressions` : `${index}.expressions`;

                    return renderItemRecursive(nestedItem, nestedIndex, nestedPath, item.expressions, newAncestorIds);
                  })}
                </SortableContext>
              </DroppableContainer>
              <Box sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
                <Typography sx={{ fontSize: 24 }}>)</Typography>
                <IconButton aria-label="Delete expression" onClick={() => onDeleteSet(item.id)}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>
          )}
        </SortableItem>
      );
    }

    const isLastInContainer = index === containerExpressions.length - 1;

    const checkLastInTree = (ids: string[], currentExpressions: QueryExpression[]) => {
      if (ids.length === 0) return false;

      return ids.every((currentId, idx) => {
        const currentArray = ids.slice(0, idx).reduce((acc, ancestorId) => {
          const foundItem = acc.find((expr) => expr.id === ancestorId);
          return foundItem?.expressions ?? acc;
        }, currentExpressions);

        const itemIndex = currentArray.findIndex(({ id }) => id === currentId);

        return itemIndex === currentArray.length - 1;
      });
    };

    const isItemLastInRoot = item.id === lastItemId;
    const isLastItemInTree = ancestorIds.length > 0 ? checkLastInTree(ancestorIds, expressions) : true;
    const isHideConjunction = isItemLastInRoot || (isLastInContainer && isLastItemInTree);

    return (
      <SortableItem key={item.id} id={item.id} isDragHandle>
        {({ dragHandleProps }) => (
          <QueryExpressionItem
            properties={properties}
            queryExpression={item}
            sx={{ borderTop: index > 0 ? 'none' : '1px solid #ccc' }}
            onChange={(field, value) => {
              const changePath = parentPath ? `${parentPath}.${index}.${field}` : `${index}.${field}`;
              onQueryExpressionChange(changePath, value ?? '');
            }}
            onDelete={() => onDeleteSet(item.id)}
            dragHandleProps={dragHandleProps}
            isConjunction={!isHideConjunction}
          />
        )}
      </SortableItem>
    );
  };

  const activeItem = activeId ? findItemById(activeId) : null;

  return (
    <Box sx={{ border: '1px solid #ccc' }}>
      <DndContext collisionDetection={collisionDetection} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <DroppableContainer id="root" isEmpty={expressions.length === 0}>
          <SortableContext items={expressions.map(({ id }) => id)} strategy={verticalListSortingStrategy}>
            {expressions.map((item, index: number) => renderItemRecursive(item, index))}
          </SortableContext>
        </DroppableContainer>
        <DragOverlay>
          {activeItem ? (
            <Box
              sx={{
                opacity: 0.8,
                backgroundColor: 'white',
                border: `2px solid #ccc`,
                borderRadius: 1,
                padding: 1,
              }}
            >
              {activeItem.expressions ? (
                <Typography>Group ({activeItem.expressions.length} items)</Typography>
              ) : (
                <Typography>
                  {activeItem.property} {activeItem.operator} {activeItem.value}
                </Typography>
              )}
            </Box>
          ) : null}
        </DragOverlay>
      </DndContext>
    </Box>
  );
};
